"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { EventAddonMenu } from "@/lib/events/addon-types";
import {
  type AddonSheetIntent,
  EventAddonsSheet,
} from "./event-addons-sheet.client";

type OpenOptions = {
  intent?: AddonSheetIntent;
  occurrenceId?: number | null;
  /** Scroll the sheet to this section. */
  sectionId?: number | null;
};

/**
 * `?menu=<sectionId>` (or `?menu=open`): a link an organiser or partner can
 * share — Loudmouth's Instagram bio — that lands on the event with its menu
 * open. Signed-out visitors can browse; adding asks them to sign in.
 */
export const ADDON_MENU_PARAM = "menu";

type EventAddonsContextValue = {
  menu: EventAddonMenu;
  /** The date selected on the page — what the sheet opens on by default. */
  previewOccurrenceId: number | null;
  open: (options?: OpenOptions) => void;
  /** Lets the booking-success moment hold its push ask until the sheet closes. */
  isOpen: boolean;
};

const EventAddonsContext = createContext<EventAddonsContextValue | null>(null);

/** Null when the event sells no add-ons — every consumer then renders nothing. */
export function useEventAddons() {
  return useContext(EventAddonsContext);
}

/**
 * One sheet per page, opened from any number of entry points (the header
 * pill, the Details section, the ticket card, a deep link). Owning it here
 * keeps them all in agreement about which ticket is being ordered for.
 */
export function EventAddonsProvider({
  menu,
  bookHref = null,
  previewOccurrenceId = null,
  children,
}: {
  menu: EventAddonMenu | null;
  bookHref?: string | null;
  previewOccurrenceId?: number | null;
  children: React.ReactNode;
}) {
  const [state, setState] = useState<{
    open: boolean;
    intent: AddonSheetIntent;
    occurrenceId: number | null;
    sectionId: number | null;
  }>({ open: false, intent: "browse", occurrenceId: null, sectionId: null });

  const open = useCallback(
    (options?: OpenOptions) =>
      setState({
        open: true,
        intent: options?.intent ?? "browse",
        occurrenceId: options?.occurrenceId ?? null,
        sectionId: options?.sectionId ?? null,
      }),
    [],
  );

  const value = useMemo(
    () =>
      menu ? { menu, previewOccurrenceId, open, isOpen: state.open } : null,
    [menu, previewOccurrenceId, open, state.open],
  );

  return (
    <EventAddonsContext.Provider value={value}>
      {children}
      {menu ? (
        // useSearchParams needs its own boundary under cacheComponents.
        <Suspense fallback={null}>
          <MenuLinkOpener menu={menu} open={open} />
        </Suspense>
      ) : null}
      {menu ? (
        <EventAddonsSheet
          menu={menu}
          open={state.open}
          intent={state.intent}
          initialOccurrenceId={state.occurrenceId ?? previewOccurrenceId}
          bookHref={bookHref}
          focusSectionId={state.sectionId}
          onOpenChange={(next) =>
            setState((current) => ({ ...current, open: next }))
          }
        />
      ) : null}
    </EventAddonsContext.Provider>
  );
}

/** Opens the sheet for `?menu=`, then strips it so a refresh doesn't reopen. */
function MenuLinkOpener({
  menu,
  open,
}: {
  menu: EventAddonMenu;
  open: (options?: OpenOptions) => void;
}) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const handledRef = useRef<string | null>(null);
  const requested = searchParams.get(ADDON_MENU_PARAM);

  useEffect(() => {
    if (!requested || handledRef.current === requested) return;
    handledRef.current = requested;
    const sectionId = Number(requested);
    open({
      sectionId: menu.sections.some((section) => section.id === sectionId)
        ? sectionId
        : null,
    });
    const next = new URLSearchParams(searchParams.toString());
    next.delete(ADDON_MENU_PARAM);
    router.replace(next.size ? `${pathname}?${next}` : pathname, {
      scroll: false,
    });
  }, [requested, menu.sections, open, searchParams, router, pathname]);

  return null;
}
